import { List, type Set } from 'immutable';
import { useState } from 'react';
import { useChatContext } from 'stream-chat-react';

import { errorNotice } from '@/notices';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import FontIcon from '@/shared/ui/FontIcon';
import { useWiwDispatch, useWiwSelector } from '@/store';
import type { HeaderButton } from '@/workchat/v2/components/HeaderV2';
import { viewStackPop } from '@/workchat/v2/store/reducer';
import HeaderV3 from '@/workchat/v3/components/HeaderV3/HeaderV3';
import { UserPickerV3 } from '@/workchat/v3/components/UserPickerV3/UserPickerV3';

export interface AddParticipantsV3Props {
  channelUserIds?: Set<string>;
}

export default function AddParticipantsV3({ channelUserIds }: AddParticipantsV3Props) {
  const dispatch = useWiwDispatch();
  const account = useWiwSelector(getAuthAccount);
  const user = useWiwSelector(getAuthUser);

  const { channel } = useChatContext();

  const [selectedUsers, setSelectedUsers] = useState(List<string>());

  const addMembersToChannel = async () => {
    try {
      const selectedUserIds = selectedUsers.toArray().map(id => `${id}`);

      await channel?.addMembers(selectedUserIds);

      dispatch(viewStackPop());
    } catch (_error) {
      dispatch(errorNotice('There was an issue adding channel members, please try again later.'));
    }
  };

  const back = () => {
    dispatch(viewStackPop());
  };

  const disabled = !selectedUsers.size;

  const buttons: HeaderButton[] = [
    {
      title: 'Back',
      action: back,
      label: [<FontIcon className="mr-1" key="back" icon="chevron-left" />, 'Back'],
      side: 'left',
      color: 'secondary',
    },
    { action: addMembersToChannel, label: 'Done', disabled, side: 'right', color: 'primary' },
  ];

  return (
    <div className="AddParticipantsV3">
      <HeaderV3 buttons={buttons}>Add Coworkers</HeaderV3>
      <UserPickerV3
        user={user}
        account={account}
        excludeUserIds={channelUserIds}
        onUserAdded={selectedUsers => setSelectedUsers(selectedUsers)}
      />
    </div>
  );
}
